import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { selectNewRecordCreationInProgress } from '@src/suite/common/store/fullscreen';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { checkRequiredProperties } from '@src/suite/common/utils/record-utils';
import { calculateDuration } from '@src/suite/common/utils/timing';
import { getRundownStartDate, getRundownTemplateStartEndTime } from '@src/suite/common/utils/util';
import { RundownService } from '@src/suite/live/services/rundown.service';
import { LiveApiActions } from '@src/suite/live/store/actions/live.actions';

import { LoaderComponent } from '../../../loader/loader';
import { RundownPropertiesComponent } from '../../components/rundown-properties/rundown-properties';

@Component({
    selector: 'st-rundown-properties-full-screen',
    imports: [LoaderComponent, MatButton, RundownPropertiesComponent],
    templateUrl: './st-rundown-properties-full-screen.component.html',
    styleUrl: './st-rundown-properties-full-screen.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-rundown-properties-full-screen',
    },
})
export class StRundownPropertiesFullScreenComponent {
    // Injects
    private store = inject(Store);
    private rundownService = inject(RundownService);

    // Fields
    private updatedFields = new Set<PSTFieldNames>();

    // Store selectors
    public languageSettings = this.store.selectSignal(selectI18N)();

    // Signals
    public readonly record: Signal<StorytellingRundownRecord>;
    public readonly localRecord = signal<StorytellingRundownRecord>(null);
    readonly popUpTitle = signal(this.languageSettings.PSTRundownCreateNewRundown);

    // Computed
    readonly loading = computed(() => this.store.selectSignal(selectNewRecordCreationInProgress)());
    readonly title = computed(() => this.localRecord()?.Title);
    readonly startTime = computed(() => this.localRecord()?.StartTime);
    readonly endTime = computed(() => this.localRecord()?.EndTime);
    readonly disableCreate = computed(() => {
        return !this.title() || !this.startTime() || !this.endTime() || this.endTime() < this.startTime() || !this.isRequiredFieldsPopulated();
    });
    readonly propertiesOptions = computed(() => {
        if (!this.localRecord()?.isNewRecord) {
            return {
                propsToIgnore: [PSTFieldNames.PSTSourceGUID],
            };
        }
        return {};
    });
    readonly rundownArchived = computed(() => this.localRecord()?.Path?.isArchivePath);

    readonly isRequiredFieldsPopulated = computed(() => {
        return checkRequiredProperties(this.localRecord(), this.rundownService);
    });

    constructor() {
        const data = inject<{
            record: StorytellingRundownRecord;
            popUpTitle: string;
        }>(MAT_DIALOG_DATA);

        const calculatedStartTime = data?.record?.StartTime ? data?.record?.StartTime : getRundownStartDate();
        const calculatedTemplateStartEndTime = getRundownTemplateStartEndTime(data?.record);
        if (data?.record?.Path?.isTemplate()) {
            this.localRecord.set({
                ...data.record,
                StartTime: calculatedTemplateStartEndTime.StartTime,
                EndTime: calculatedTemplateStartEndTime.EndTime,
            });
        } else {
            const calculatedEndTime = data?.record?.EndTime ? data?.record?.EndTime : calculatedStartTime.plus({ minutes: 30 });
            this.localRecord.set({
                ...data.record,
                StartTime: calculatedStartTime,
                EndTime: calculatedEndTime,
                MOSroEdStart: calculatedStartTime,
                MOSroEdDur: calculateDuration(calculatedStartTime, calculatedEndTime),
                AsRunArchiveInterval: 30,
            });
        }
        this.popUpTitle.set(data.popUpTitle);
    }

    // Methods
    onRecordChanged(record: StorytellingRundownRecord) {
        this.localRecord.set(record);
    }

    create() {
        if (this.localRecord()?.isNewRecord) {
            this.store.dispatch(LiveApiActions.createNewRundown({ record: this.localRecord() }));
        } else {
            this.store.dispatch(
                RecordHttpActions.saveRecordChanges({ fieldsToUpdate: Array.from(this.updatedFields).map(f => ({ key: f, value: this.localRecord()[f] })), record: this.localRecord() })
            );
        }
        this.updatedFields.clear();
    }

    closeModal() {
        this.store.dispatch(LiveApiActions.closeNewRundownDialog());
        this.updatedFields.clear();
    }

    propertyChanged(event: { field: PSTFieldNames; value: any }) {
        this.updatedFields.add(event.field);
    }
}
